import type {
  ApiEnvelope,
  AuthTokens,
  CompletePasswordlessLoginInput,
  PasswordlessCompleteData,
  AuthResponse,
  Permission,
  PermissionAction,
  PermissionResource,
  User,
  UserRole,
} from "@/lib/types/auth.types"
import { isHydrated, makePersistable } from "mobx-persist-store"
import { makeAutoObservable, runInAction } from "mobx"

import { ApiService } from "@/lib/api"
import { AxiosError, isAxiosError } from "axios"
import { GET_ME } from "@/lib/graphql/queries/auth.queries"
import { ROLE_PERMISSIONS } from "@/config/menu"
import type { RootStore } from "./root-store"
import { apolloClient } from "@/lib/graphql/client"
import { clearPushDeviceOnLogout } from "@/lib/push/logout"
import { normalizeRole } from "@/lib/utils/role"
import { toastUtils } from "@/utils/toast-utils"

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

const hasStatus = <T>(response: ApiEnvelope<T> | T): response is ApiEnvelope<T> =>
  isObject(response) && "status" in response

const extractData = <T>(response: ApiEnvelope<T> | T): T => {
  if (isObject(response) && "data" in response) {
    return (response as ApiEnvelope<T>).data as T
  }
  return response as T
}

const isSuccessful = <T>(response: ApiEnvelope<T> | T): boolean =>
  !hasStatus(response) || response.status !== false

const normalizeTokens = (data: PasswordlessCompleteData): AuthTokens | null => {
  if (typeof data.access_token === "string") {
    return { access_token: data.access_token }
  }
  if (isObject(data.access_token)) {
    return data.access_token as AuthTokens
  }
  if (data.accessToken) {
    return {
      accessToken: data.accessToken,
      tokenType: data.tokenType,
      expiresIn: data.expiresIn,
    }
  }
  if (data.token) {
    return { token: data.token }
  }
  return null
}

const getApiErrorMessage = (value: unknown): string | null => {
  if (!isObject(value)) return null

  // Check standard backend format: { response: { message } }
  const resp = value.response
  if (isObject(resp)) {
    const respMsg = (resp as Record<string, unknown>).message
    if (typeof respMsg === "string" && respMsg.trim()) {
      return respMsg.trim()
    }
  }

  const data = value.data
  if (isObject(data)) {
    const dataResp = (data as Record<string, unknown>).response
    if (isObject(dataResp)) {
      const respMsg = (dataResp as Record<string, unknown>).message
      if (typeof respMsg === "string" && respMsg.trim()) {
        return respMsg.trim()
      }
    }
    const nestedMessage = data.message
    if (typeof nestedMessage === "string" && nestedMessage.trim()) {
      return nestedMessage
    }
    if (Array.isArray(nestedMessage)) {
      const firstMessage = nestedMessage.find((item) => typeof item === "string" && item.trim())
      if (typeof firstMessage === "string") {
        return firstMessage
      }
    }
  }

  const message = value.message
  if (typeof message === "string" && message.trim()) {
    return message
  }

  return null
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message.trim()) {
    return error.message
  }

  if (isObject(error)) {
    const response = error.response
    const apiMessage = getApiErrorMessage(response) ?? getApiErrorMessage(error)
    if (apiMessage) {
      return apiMessage
    }
  }

  return fallback
}

type MeQueryResponse = {
  me?: User | null
}

const normalizeUserFromGraphQL = (user: User): User => {
  const location = user.profile?.location
  if (!location || !user.profile) return user

  return {
    ...user,
    profile: {
      ...user.profile,
      address: user.profile.address ?? location.address,
      city: user.profile.city ?? location.city,
      state: user.profile.state ?? location.state,
      country: user.profile.country ?? location.country,
      zipCode: user.profile.zipCode ?? location.zipCode,
    },
  }
}

export class AuthStore {
  rootStore: RootStore
  isAuthenticated: boolean
  user: User | null
  role: UserRole | null
  permissions: Permission[]
  tokens: AuthTokens | null
  isLoading: boolean
  error: string | null
  redirectAfterLogin: string
  pendingEmail: string | null
  otpSent: boolean
  otpVerifying: boolean
  email: string | null

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    this.isAuthenticated = false
    this.user = null
    this.role = null
    this.permissions = []
    this.tokens = null
    this.isLoading = false
    this.error = ''
    this.redirectAfterLogin = "/"
    this.pendingEmail = null
    this.otpSent = false
    this.otpVerifying = false
    this.email = null

    makeAutoObservable(this, {
      rootStore: false,
    })

    makePersistable(this, {
      name: "AuthStore",
      properties: ["isAuthenticated", "user", "role", "permissions", "tokens"],
      storage: typeof window !== "undefined" ? window.localStorage : undefined,
    })

    // Keep auth data in localStorage only and sync across tabs.
    if (typeof window !== "undefined") {
      window.addEventListener("storage", (e) => {
        if (e.key === "AuthStore") {
          if (e.newValue) {
            try {
              const storedData = JSON.parse(e.newValue)
              runInAction(() => {
                this.isAuthenticated = storedData.isAuthenticated ?? false
                this.user = storedData.user ?? null
                this.role = storedData.role ?? null
                this.permissions = storedData.permissions ?? []
                this.tokens = storedData.tokens ?? null
              })
            } catch (error) {
              console.error("Error syncing auth state from storage:", error)
            }
          } else {
            this.clearAuthState()
          }
        }

        if (e.key === "auth_tokens" && e.newValue) {
          try {
            const parsedTokens = JSON.parse(e.newValue)
            runInAction(() => {
              this.tokens = parsedTokens
            })
          } catch (error) {
            console.error("Error syncing tokens from storage:", error)
          }
        }
      })
    }
  }

  syncTokensWithStorage() {
    if (this.tokens && typeof window !== "undefined") {
      localStorage.setItem("auth_tokens", JSON.stringify(this.tokens))
    }
  }

  private syncAuthStateWithStorage() {
    if (typeof window === "undefined") return
    localStorage.setItem(
      "AuthStore",
      JSON.stringify({
        isAuthenticated: this.isAuthenticated,
        user: this.user,
        role: this.role,
        permissions: this.permissions,
        tokens: this.tokens,
      })
    )
  }

  get isHydrated() {
    return isHydrated(this)
  }

  async hydrate() { }

  setRedirectAfterLogin(path: string) {
    this.redirectAfterLogin = path
  }

  private clearAuthState() {
    runInAction(() => {
      this.isAuthenticated = false
      this.user = null
      this.role = null
      this.permissions = []
      this.tokens = null
      this.pendingEmail = null
      this.otpSent = false
      this.otpVerifying = false
      this.email = null
    })
  }

  async requestOTP(email: string, options?: { autoRegister?: boolean }) {
    this.isLoading = true
    this.error = null
    this.email = email

    try {
      const response = await ApiService.requestPasswordlessLogin({
        email,
        autoRegister: options?.autoRegister,
      })

      if (isSuccessful(response)) {
        const data = extractData<{ code?: string }>(response) ?? {}
        runInAction(() => {
          this.pendingEmail = email
          this.otpSent = true
          this.isLoading = false
        })
        console.log('returned data', data)
        toastUtils.success("OTP Sent", `A verification code has been sent to ${email}`)
        return { success: true, token: data.code ?? null }
      }

      const errorMessage = getApiErrorMessage(response) || "Email not found. Please check your email address."
      throw new Error(errorMessage)
    } catch (error) {
      const errorMessage = getErrorMessage(error, "An error occurred while requesting OTP")
      const is404 = isAxiosError(error) && error.response?.status === 404
      const isNotRegistered = is404 || errorMessage.toLowerCase().includes("not registered")

      runInAction(() => {
        this.isLoading = false
        this.error = isNotRegistered ? null : errorMessage
      })

      if (isNotRegistered) {
        return { success: false, notRegistered: true, error: errorMessage }
      }

      toastUtils.error("OTP Request Failed", errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  private applyAuthResponse(data: PasswordlessCompleteData) {
    const user = data.user
    if (!user) {
      throw new Error("Invalid authentication response")
    }

    const role = normalizeRole(user.staff?.role) ?? "customer"
    const rolePermissions = ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS.customer

    runInAction(() => {
      this.isAuthenticated = true
      this.email = null
      this.isLoading = false
      this.user = user
      this.role = role
      this.permissions = rolePermissions.map((p) => ({ ...p, actions: [...p.actions] }))
      this.tokens = normalizeTokens(data)
      this.pendingEmail = null
      this.otpSent = false
      this.otpVerifying = false
      this.error = null
      this.syncTokensWithStorage()
      this.syncAuthStateWithStorage()
    })
  }

  private toPasswordlessData(
    response: AuthResponse | ApiEnvelope<PasswordlessCompleteData> | PasswordlessCompleteData,
  ): PasswordlessCompleteData {
    if (isObject(response) && "accessToken" in response && "user" in response) {
      const authResponse = response as AuthResponse
      return {
        user: authResponse.user,
        accessToken: authResponse.accessToken,
        tokenType: authResponse.tokenType,
        expiresIn: authResponse.expiresIn,
      }
    }

    if (hasStatus(response)) {
      return extractData<PasswordlessCompleteData>(response) ?? {}
    }

    return response as PasswordlessCompleteData
  }

  async verifyOTP(otp: string) {
    if (!this.pendingEmail) {
      return { success: false, error: "No pending email verification" }
    }

    this.otpVerifying = true
    this.error = null

    try {
      const payload: CompletePasswordlessLoginInput = {
        email: this.pendingEmail,
        code: otp,
      }
      const response = await ApiService.completePasswordlessLogin(payload)

      if (isSuccessful(response)) {
        const data = this.toPasswordlessData(response)
        this.applyAuthResponse(data)
        await this.refreshMeFromGraphQL()
        const firstName = data.user?.profile?.firstName ?? "there"
        toastUtils.success(`Welcome back, ${firstName}!`, "Login successful")
        return { success: true }
      }

      const errorMessage = getApiErrorMessage(response) || "Invalid OTP code"
      throw new Error(errorMessage)
    } catch (error) {
      const errorMessage = getErrorMessage(error, "An error occurred while verifying OTP")
      runInAction(() => {
        this.otpVerifying = false
        this.error = errorMessage
      })
      toastUtils.error("Verification Failed", errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      runInAction(() => {
        this.otpVerifying = false
      })
    }
  }

  async loginWithGoogle() {
    this.isLoading = true
    this.error = null

    try {
      const { getFirebaseAuth, googleAuthProvider, isFirebaseConfigured } =
        await import("@/lib/firebase/client")
      const { signInWithPopup } = await import("firebase/auth")

      if (!isFirebaseConfigured()) {
        throw new Error(
          "Firebase is not configured. Add NEXT_PUBLIC_FIREBASE_* variables to .env.local.",
        )
      }

      const auth = getFirebaseAuth()
      const credential = await signInWithPopup(auth, googleAuthProvider)
      const idToken = await credential.user.getIdToken()
      const response = await ApiService.loginWithGoogle({ idToken })

      if (!isSuccessful(response)) {
        const errorMessage = getApiErrorMessage(response) || "Google sign-in failed"
        throw new Error(errorMessage)
      }

      const data = this.toPasswordlessData(response)
      this.applyAuthResponse(data)
      await this.refreshMeFromGraphQL()
      const firstName = data.user?.profile?.firstName ?? "there"
      toastUtils.success(`Welcome, ${firstName}!`, "Signed in with Google")
      return { success: true }
    } catch (error: any) {
      // User closed the popup before completing sign-in
      if (
        error?.code === "auth/popup-closed-by-user" ||
        error?.message?.includes("auth/popup-closed-by-user")
      ) {
        runInAction(() => {
          this.isLoading = false
          this.error = null
        })
        return { success: false, cancelled: true }
      }

      if (
        error?.code === "auth/popup-blocked" ||
        error?.message?.includes("auth/popup-blocked")
      ) {
        const msg = "Popup was blocked by your browser. Please allow popups for this site and try again."
        runInAction(() => {
          this.isLoading = false
          this.error = msg
        })
        toastUtils.error("Popup Blocked", msg)
        return { success: false, error: msg }
      }

      if (
        error?.code === "auth/unauthorized-domain" ||
        error?.message?.includes("auth/unauthorized-domain")
      ) {
        const msg = "This domain is not authorized for OAuth operations in your Firebase project. Please add it to Authorized Domains in the Firebase Console."
        runInAction(() => {
          this.isLoading = false
          this.error = msg
        })
        toastUtils.error("Unauthorized Domain", msg)
        return { success: false, error: msg }
      }

      const errorMessage = getErrorMessage(error, "Google sign-in failed")
      runInAction(() => {
        this.isLoading = false
        this.error = errorMessage
      })
      toastUtils.error("Google Sign-In Failed", errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  resetOTPFlow() {
    this.pendingEmail = null
    this.otpSent = false
    this.otpVerifying = false
    this.error = null
  }

  async logout() {
    try {
      this.isLoading = true
      await clearPushDeviceOnLogout()
      await new Promise((resolve) => setTimeout(resolve, 500))
      this.clearAuthState()
      runInAction(() => {
        this.isLoading = false
        this.error = null
        this.redirectAfterLogin = "/dashboard"
      })
      this.clearAuthStorage()
      toastUtils.info("Logged Out", "You have been successfully logged out.")
      return { success: true, error: null }
    } catch (error) {
      const errorMessage = getErrorMessage(error, "An error occurred while logging out")
      this.isLoading = false
      runInAction(() => {
        this.error = errorMessage
      })
      return { success: false, error: errorMessage }
    }
  }

  /** Clears auth memory + storage without toasts or redirects. */
  private clearAuthStorage() {
    if (typeof window === "undefined") return
    localStorage.removeItem("auth_tokens")
    localStorage.removeItem("AuthStore")
    localStorage.removeItem("authToken")
    sessionStorage.removeItem("auth_tokens")
    sessionStorage.removeItem("AuthStore")
  }

  /**
   * Silently end the session (invalid/expired token).
   * No toasts — callers may redirect via AuthGuard or navigation.
   */
  silentLogout() {
    void clearPushDeviceOnLogout().catch(() => {})
    this.clearAuthState()
    runInAction(() => {
      this.isLoading = false
      this.error = null
    })
    this.clearAuthStorage()
  }

  /**
   * Validate the stored token against GET /auth/me (user details).
   * On 401/403: silentLogout. Network/server errors: leave session alone.
   */
  async validateSessionOnFocus(): Promise<boolean> {
    if (!this.isAuthenticated) return false

    const accessToken = this.getAccessToken()
    if (!accessToken) {
      this.silentLogout()
      return false
    }

    try {
      const response = await fetch("/api/auth/me", {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        cache: "no-store",
      })

      if (response.status === 401 || response.status === 403) {
        this.silentLogout()
        return false
      }

      if (!response.ok) {
        return true
      }

      return true
    } catch {
      // Offline / network blip — do not log out
      return true
    }
  }

  async checkSession() {
    return this.isAuthenticated && !!this.tokens
  }

  private getAccessToken() {
    if (!this.tokens) return null
    return (
      this.tokens.access_token ??
      this.tokens.accessToken ??
      this.tokens.token ??
      null
    )
  }

  async refreshMeFromGraphQL() {
    if (!this.isAuthenticated) return null

    const accessToken = this.getAccessToken()
    if (!accessToken) return null

    try {
      const { data } = await apolloClient.query<MeQueryResponse>({
        query: GET_ME,
        fetchPolicy: "network-only",
        context: {
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
        },
      })

      const me = data?.me
      if (!me) return null
      const normalizedMe = normalizeUserFromGraphQL(me)

      const role =
        normalizeRole(normalizedMe.staff?.role) ??
        "customer"
      const rolePermissions = ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS.customer

      runInAction(() => {
        this.user = normalizedMe
        this.role = role
        this.permissions = rolePermissions.map((p) => ({ ...p, actions: [...p.actions] }))
        this.syncAuthStateWithStorage()
      })

      return normalizedMe
    } catch (error) {
      console.error("Failed to refresh current user from GraphQL me query:", error)
      return null
    }
  }

  hasPermission(resource: PermissionResource, action: PermissionAction): boolean {
    if (!this.isAuthenticated || !this.role) {
      return false
    }
    // Superadmin has all permissions
    if (this.role === 'superadmin') {
      return true
    }
    // Check if user has the specific permission
    return this.permissions.some(
      (permission) =>
        permission.resource === resource && permission.actions.includes(action),
    )
  }

  hasRole(...roles: string[]): boolean {
    if (!this.isAuthenticated || !this.role) {
      return false
    }
    return roles.includes(this.role)
  }

  get fullName(): string {
    if (!this.user?.profile) {
      return ""
    }
    return `${this.user?.profile?.firstName} ${this.user?.profile?.lastName}`
  }
}
